<template>
	<div class="overflow-hidden flex-1 flex flex-col bg-surface-50 dark:bg-surface-950">
		<div
        class="flex items-stretch overflow-hidden flex-1 divide-x divide-gray-300 dark:divide-surface-600"
        :class="{
          'translate-x-0 w-[200%] md:translate-x-0 md:w-full': indexRouteName && showingList,
          '-translate-x-[50%] w-[200%] md:translate-x-0 md:w-full': indexRouteName && !showingList,
        }"
    >
			<div class="flex flex-col md:w-80 lg:w-80 xl:w-96" :class="{'w-[50%]': indexRouteName}">

				<!-- Search header at top of the list -->
				<div class="bg-surface-0 dark:bg-surface-800 p-4 pt-2 flex flex-col sticky top-0">
					<div class="flex flex-row space-x-2 mt-2">
						<div class="flex-1">
							<InputGroup>
								<InputText
								:pt="{ root: { ...(searchAdminPage ? { 'data-cy': searchAdminPage } : {}) } }"
							    :pt-options="{ mergeAttrs: true }" 
										:placeholder="placeholderValue || `${$t('common.search')}...`"
										v-model="searchValue"
								/>
							</InputGroup>
						</div>

						<Button v-if="hasOnRefreshListener" :label="$t('common.refresh')" @click="$emit('on-refresh')">
							<template #icon>
								<IconRefresh class="w-4 h-4 mr-1.5" :stroke-width="3" />
							</template>
						</Button>
						<Button v-if="hasOnCreateListener && hasCreatePermission" :label="$t('common.create')"
							@click="$emit('on-create')"
							:pt="{ root: { ...(createButtonAdminPage ? { 'data-cy': createButtonAdminPage } : {}) } }"
							:pt-options="{ mergeAttrs: true }">
							<template #icon>
								<IconPlus class="w-4 h-4 mr-1.5" :stroke-width="3" />
							</template>
						</Button>

            <Button v-if="hasOnFilterListener" severity="secondary" @click="showFilterDialog">
              <template #icon>
                <IconFilter class="w-4 h-4" :stroke-width="3"/>
              </template>
            </Button>
					</div>
        </div>

        <div v-if="filterBadgeCount" class="text-sm text-primary-500 font-medium px-4 pt-0 pb-2 bg-white flex items-center justify-end">
          <span>{{ filterBadgeCount }} active filters</span>
        </div>

				<!-- List Items -->
				<div class="overflow-scroll flex-1 bg-surface-50 dark:bg-surface-900 border-t">
					<ul class="divide-y divide-gray-100 dark:divide-gray-700">
						<slot name="list"></slot>
					</ul>
				</div>

				<!-- Footer for pagination -->
				<div v-if="store?.data"
				     class="sticky bottom-0 bg-surface-0 dark:bg-surface-800">
					<Paginator
							class="pb-4"
							:rows="store.data?.per_page"
							:total-records="store.data?.total"
							@page="($event) => onPaginate($event.page + 1)"
							:page-link-size="3"
					/>
				</div>

			</div>

			<div class="flex-1 overflow-scroll">
        <div class="md:hidden bg-white px-4 pt-5 -mt-2 pb-2" v-if="indexRouteName && !showingList">
          <router-link :to="{ name: indexRouteName }" class="inline-flex items-center px-2 py-2 bg-surface-0 rounded-md hover:bg-surface-100">
              <i :class="['ti ti-arrow-back-up text-xl', 'text-surface-400 mr-1 mt-1']"/>
              <span class="text-surface-500 dark:text-surface-400 font-semibold">Back</span>
          </router-link>
        </div>

				<slot name="detail"></slot>
			</div>
		</div>
	</div>
</template>

<script>
import PageHeading from "@/Components/PageHeading.vue";
import InputGroup from "primevue/inputgroup";
import InputGroupAddon from "primevue/inputgroupaddon";
import Paginator from "primevue/paginator";

import debounce from 'lodash/debounce';
import { IconSearch, IconPlus, IconRefresh, IconFilter } from '@tabler/icons-vue';
import EditFiltersForm from "@/Forms/Filters/EditFiltersForm.vue";

export default {
	name: "ListDetailLayout",
	components: {
		InputGroup,
		InputGroupAddon,
		Paginator,
		PageHeading,
		IconSearch,
		IconPlus,
		IconRefresh,
		IconFilter,
	},
	data() {
		return {
			searchValue: null,
			showingList: true,
		}
	},
	props: {
		sticky: Boolean,
		placeholderValue: String,
		store: {
			type: Object,
		},
		createPermissions: { type: String },
		indexRouteName: String,
		filter: Object,
		createButtonAdminPage: String,
		searchAdminPage: String,
	},
	computed: {
		hasOnRefreshListener() {
			return !!this.$attrs.onOnRefresh;
		},
		hasOnCreateListener() {
			return !!this.$attrs.onOnCreate;
		},
		hasOnFilterListener() {
			return !!this.$attrs.onOnFilter;
		},
		hasCreatePermission() {
			return this.createPermissions !== undefined && this.$auth.hasPermission(this.createPermissions);
		},
		showingList() {
			return this.indexRouteName === this.$route.name;
		},
		filterBadgeCount() {
			if (!this.filter)
				return;

			let count = 0;
			for (const [key, value] of Object.entries(this.filter)) {
				if (Array.isArray(value)) {
					if (value.length > 0) {
						console.log('length is > 0')
						count++
					}
				}

				else if (value !== null) {
					console.log('value is not null')
					count++
				}
			}
			return count;
		}
	},
	methods: {
		onPaginate(page) {
			this.$emit('on-paginate', page);
		},
		onSearch: debounce(function (newValue) {
			this.$emit('on-search', newValue);
		}, 500),
		showFilterDialog() {
			this.$dialog.open(EditFiltersForm, {
				data: {
					filter: this.filter,
				},
				props: {
					header: this.$t('forms.filters.all_filters_heading'),
					style: {
						maxHeight: '38rem',
						width: '50rem',
						maxWidth: '50rem',
					},
					breakpoints: {
						'960px': '75vw',
						'640px': '90vw'
					},
					modal: true
				},
				onClose: (opt) => {
					console.log(opt);
					if (opt.data?.filter) {
						this.$emit('on-filter', opt.data);
					}
				},
			})
		}
	},
	watch: {
		searchValue(newValue, oldValue) {
			this.onSearch(newValue);
		}
	},
}
</script>

<style scoped></style>
